import { useBemCN } from "@gik/core/utils/bemBlock";
import withComponentErrorBoundary from "@gik/core/utils/withComponentErrorBoundary";
import { Animation } from "@gik/ui-legacy/Animation";
import { Layout } from "@gik/ui-legacy/gik/Layout/Layout";
import { Image } from "@gik/ui-legacy/Image";
import { Separator } from "@gik/ui-legacy/Separator";
import { SvgIcon } from "@gik/ui-legacy/SvgIcon";
import CheckIcon from "@heroicons/react/solid/CheckIcon";
import React from "react";
import type { LandingPageFeaturesFocus } from "../types/LandingPageFeatureFocus";

export interface IFeatureFocusProps extends LandingPageFeaturesFocus {}

/**
 * https://www.figma.com/design/FT2ninjhNiQOXfnOTaZfqy/%F0%9F%91%A8%F0%9F%8F%BC%E2%80%8D%F0%9F%8E%A4-Jade's-workspace-of-asset-creation?node-id=4939-10958&m=dev
 */
function FeatureFocusComp({
	title,
	subtitle,
	icon,
	iconStyles,
	animation,
	features,
}: IFeatureFocusProps) {
	const bem = useBemCN("feature-focus");
	return (
		<Layout
			direction={"column md:row"}
			align={"start md:center"}
			perpendicular={"center"}
			{...bem()}
		>
			<aside
				style={{
					flex: "0 0 50%",
				}}
			>
				{icon && (
					<Image
						src={icon}
						alt={"Icon"}
						className={"tw-w-full"}
						style={iconStyles}
					/>
				)}
				{animation && (
					<Animation
						mode={"looped"}
						data={animation.data}
						assetsPrepend={animation.assetsPrepend}
					/>
				)}
			</aside>
			<Layout
				element={"main"}
				direction={"column"}
				gap={0.5}
				style={{
					flex: "1 1 50%",
				}}
			>
				<h2 className={titleClassNames.join(" ")}>{title}</h2>
				{subtitle && <p className={subtitleClassNames.join(" ")}>{subtitle}</p>}
				<Separator />
				<ul className={featureListClassNames.join(" ")}>
					{features.map((feature, i) => (
						<Layout
							direction={"row"}
							align={"start"}
							perpendicular={"center"}
							element={"li"}
							key={i}
							className={featureItemClassNames.join(" ")}
						>
							<Layout
								element={"aside"}
								inline
								align={"center"}
								perpendicular={"center"}
								className={
									"tw-mr-2 tw-rounded-full tw-bg-info-100 tw-text-info-600"
								}
								flex={"0 0 1rem"}
								style={{
									height: "1rem",
								}}
							>
								<SvgIcon width={"8px"} Icon={CheckIcon} />
							</Layout>
							<main>{feature}</main>
						</Layout>
					))}
				</ul>
			</Layout>
		</Layout>
	);
}

const titleClassNames = [
	"tw-font-heading",
	"tw-font-semibold",
	"tw-text-neutral-900",
	"tw-text-3xl",
	"tw-leading-tight",
	"md:tw-text-5xl",
	"md:tw-font-semibold",
];

const subtitleClassNames = [
	"tw-text-neutral-700",
	"tw-font-body",
	"tw-text-base",
	"tw-font-normal",
	"tw-leading-6",
	"md:tw-text-lg",
	"md:tw-font-normal",
];

const featureListClassNames = [
	"tw-text-gray-800",
	"tw-font-body",
	"tw-text-base",
	"tw-font-medium",
	"tw-leading-6",
];

const featureItemClassNames = ["tw-mb-4"];

export const FeatureFocus = withComponentErrorBoundary(FeatureFocusComp);
